import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { activityService } from "@/api/activityService";
import type { FetchActivityParams } from "@/types/activity";

export const ACTIVITY_PAGE_LIMIT = 25;

type UseActivityParams = FetchActivityParams & {
  enabled?: boolean;
};

export const activityQueryKeys = {
  all: ["activityData"] as const,
  lists: () => [...activityQueryKeys.all, "list"] as const,
  list: (
    params: Pick<
      FetchActivityParams,
      "limit" | "search" | "status" | "startDate" | "endDate"
    >,
  ) =>
    [...activityQueryKeys.lists(), params] as const,
};

export const useActivity = (params: UseActivityParams = {}) => {
  const normalizedParams = {
    limit: params.limit ?? ACTIVITY_PAGE_LIMIT,
    search: params.search?.trim() || "",
    status: params.status,
    startDate: params.startDate,
    endDate: params.endDate,
  };

  return useInfiniteQuery({
    queryKey: activityQueryKeys.list(normalizedParams),
    queryFn: ({ pageParam }) =>
      activityService.fetchActivity({
        ...normalizedParams,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    placeholderData: keepPreviousData,
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: params.enabled ?? true,
  });
};
